import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { UploadCloud, FileSpreadsheet, Sparkles, FileText } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Two-mode importer.
 *  - Classic: legacy free-form Excel/CSV using human columns (via, mq, prezzo…)
 *  - SCR: raw scraper export whose long dotted headers are mapped to Property
 *    fields via the editable mapping in Settings → Import SCR.
 */
const ImportDialog = ({ open, onOpenChange, onDone, boardId }) => {
  const fileRef = useRef();
  const [mode, setMode] = useState("classic"); // "classic" | "scr"
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const reset = () => { setFile(null); setUploading(false); };
  const close = (v) => { onOpenChange(v); if (!v) reset(); };

  const submit = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("board_id", boardId);
      const path = mode === "scr" ? "/properties/import-scr" : "/properties/import";
      const res = await api.post(path, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const extra = mode === "scr" && res.data.mapped_columns != null ? ` · ${res.data.mapped_columns} colonne mappate` : "";
      toast.success(`Importati ${res.data.inserted} · Ignorati ${res.data.skipped}${extra}`);
      onDone?.();
      close(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore importazione");
    } finally {
      setUploading(false);
    }
  };

  const TabBtn = ({ id, icon: Icon, label, sub }) => (
    <button
      type="button"
      onClick={() => { setMode(id); setFile(null); }}
      className={`flex-1 rounded-2xl px-4 py-3 border transition text-left ${
        mode === id
          ? "border-[#0A84FF] bg-[#0A84FF]/5 ring-1 ring-[#0A84FF]/40"
          : "border-slate-200 bg-white hover:border-slate-300"
      }`}
      data-testid={`import-tab-${id}`}
    >
      <div className="flex items-center gap-2">
        <Icon className={`w-4 h-4 ${mode === id ? "text-[#0A84FF]" : "text-slate-500"}`} />
        <div className={`font-semibold text-sm ${mode === id ? "text-[#0A84FF]" : "text-slate-800"}`}>{label}</div>
      </div>
      <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-lg rounded-2xl border border-slate-200 bg-white">
        <DialogHeader>
          <DialogTitle className="font-display font-black text-2xl tracking-tight">Importa immobili</DialogTitle>
        </DialogHeader>

        <div className="flex gap-3">
          <TabBtn id="classic" icon={FileText} label="Import classico" sub="Excel/CSV con colonne come via, mq, prezzo" />
          <TabBtn id="scr"     icon={Sparkles} label="Import SCR"      sub="Export SCR (intestazioni dotted)" />
        </div>

        <div className="space-y-4 mt-4">
          <div className="border border-dashed border-slate-300 p-6 text-center bg-slate-50 rounded-2xl">
            <FileSpreadsheet className="w-8 h-8 mx-auto text-slate-400" />
            <p className="mt-3 text-sm text-slate-600">Trascina un file .xlsx / .csv oppure</p>
            <input
              ref={fileRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="import-file-input"
            />
            <Button
              variant="outline"
              className="rounded-full mt-3"
              onClick={() => fileRef.current?.click()}
              data-testid="import-pick-btn"
            >
              <UploadCloud className="w-4 h-4 mr-2" /> Scegli file
            </Button>
            {file && <div className="mt-3 text-xs text-slate-700 font-mono-num">{file.name}</div>}
          </div>

          {mode === "classic" ? (
            <div className="bg-slate-100 border border-slate-200 p-3 text-[11px] leading-relaxed text-slate-600 rounded-xl">
              <div className="font-semibold text-slate-900 mb-1">Colonne riconosciute</div>
              citta, macro_area, via (o indirizzo), piano, mq, prezzo, link, ristrutturato, ascensore, "agenzia / privato", scostamento.
              <br />Colonne minime: <strong>via</strong>, <strong>mq</strong>, <strong>prezzo</strong>.
            </div>
          ) : (
            <div className="bg-[#0A84FF]/5 border border-[#0A84FF]/20 p-3 text-[11px] leading-relaxed text-slate-700 rounded-xl">
              <div className="font-semibold text-slate-900 mb-1">Import SCR</div>
              L'export SCR ha intestazioni tipo <code>price/formattedValue</code>, <code>properties/0/surface</code>, … Solo le colonne mappate vengono estratte.
              <br />Il mapping è configurabile dal super-admin in <b>Setting → Import SCR</b>.
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => close(false)}>Annulla</Button>
          <Button
            className="rounded-full bg-[#007AFF] hover:bg-[#0060df]"
            onClick={submit}
            disabled={!file || uploading}
            data-testid="import-submit-btn"
          >
            {uploading ? "Import…" : (mode === "scr" ? "Importa SCR" : "Importa")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportDialog;
